import { UserButton } from '@clerk/react'
import { createContext, type ReactNode, useContext, useLayoutEffect, useState } from 'react'
import { getActiveVariant, setActiveVariant, VARIANTS, type VariantId } from '../lib/variants'
import { DESIGNS } from './registry'
import type { DesignModule } from './types'

const DesignContext = createContext<DesignModule>(DESIGNS.v1)

/** The active design module (Shell + screens). Routes use this to pick their screen. */
export const useDesign = () => useContext(DesignContext)

const readActive = () => getActiveVariant(typeof window !== 'undefined' ? window.location.search : '')

/**
 * Always-present shared chrome: a thin strip with the language switcher + user menu,
 * rendered ABOVE the active design's Shell so you can switch languages even when a
 * language's Shell is broken or unfinished. Sets `data-design` on <html> to swap tokens.
 */
export function DesignRoot({ children }: { children: ReactNode }) {
  const [active, setActive] = useState<VariantId>(readActive)

  useLayoutEffect(() => {
    document.documentElement.dataset.design = active
  }, [active])

  const design = DESIGNS[active]
  return (
    <DesignContext.Provider value={design}>
      <div className="flex h-full flex-col">
        <div className="flex items-center justify-between border-b border-neutral-500/40 bg-neutral-900 px-3 py-1 text-xs text-neutral-200">
          <label>
            Design language{' '}
            <select
              value={active}
              onChange={(e) => {
                setActiveVariant(e.target.value)
                setActive(e.target.value as VariantId)
              }}
              className="rounded bg-neutral-800 px-1 py-0.5"
            >
              {VARIANTS.map((v) => (
                <option key={v} value={v}>
                  {v} - {DESIGNS[v].name}
                </option>
              ))}
            </select>
          </label>
          <UserButton />
        </div>
        <div className="min-h-0 flex-1">
          <design.Shell>{children}</design.Shell>
        </div>
      </div>
    </DesignContext.Provider>
  )
}
